<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import draggable from 'vuedraggable';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxKanbanAPI from 'dashboard/api/inbox/kanban';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    default: null,
  },
  activeConversationId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['open']);

const columns = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const newColumnName = ref('');
const currentRole = useMapGetter('getCurrentRole');
const inboxes = useMapGetter('inboxes/getAllInboxes');
const selectedInboxId = ref(props.inboxId);

const statusOptions = ['open', 'pending', 'snoozed', 'resolved'];

const isAdmin = computed(() => currentRole.value === 'administrator');
const effectiveInboxId = computed(() => selectedInboxId.value || inboxes.value?.[0]?.id);

const fetchBoard = async () => {
  if (!effectiveInboxId.value) return;

  isLoading.value = true;
  try {
    const response = await InboxKanbanAPI.board(effectiveInboxId.value);
    columns.value = response?.data?.payload || [];
  } catch {
    useAlert('Could not load Kanban board');
  } finally {
    isLoading.value = false;
  }
};

const openConversation = conversation => {
  emit('open', conversation);
};

const moveConversation = async ({ conversationId, targetColumnId }) => {
  try {
    await InboxKanbanAPI.moveConversation(conversationId, {
      kanban_column_id: targetColumnId,
    });
  } catch {
    useAlert('Could not move conversation');
    await fetchBoard();
  }
};

const onCardAdded = async (event, column) => {
  const movedConversation = column.conversations[event.newIndex];
  if (!movedConversation) return;

  await moveConversation({
    conversationId: movedConversation.id,
    targetColumnId: column.id,
  });
};

const addColumn = async () => {
  if (!newColumnName.value.trim() || !effectiveInboxId.value) return;

  isSaving.value = true;
  try {
    await InboxKanbanAPI.createColumn(effectiveInboxId.value, {
      name: newColumnName.value.trim(),
      mapped_status: 'open',
      position: columns.value.length,
      active: true,
    });
    newColumnName.value = '';
    await fetchBoard();
  } catch {
    useAlert('Could not create column');
  } finally {
    isSaving.value = false;
  }
};

const updateColumn = async column => {
  if (!effectiveInboxId.value) return;

  isSaving.value = true;
  try {
    await InboxKanbanAPI.updateColumn(effectiveInboxId.value, column.id, {
      name: column.name,
      mapped_status: column.mapped_status,
      position: column.position,
      color: column.color,
      active: column.active,
    });
  } catch {
    useAlert('Could not update column');
    await fetchBoard();
  } finally {
    isSaving.value = false;
  }
};

const deleteColumn = async column => {
  if (!effectiveInboxId.value) return;

  isSaving.value = true;
  try {
    await InboxKanbanAPI.deleteColumn(effectiveInboxId.value, column.id);
    await fetchBoard();
  } catch {
    useAlert('Could not delete column');
  } finally {
    isSaving.value = false;
  }
};

const onColumnsReordered = async () => {
  if (!isAdmin.value || !effectiveInboxId.value) return;

  isSaving.value = true;
  try {
    await Promise.all(
      columns.value.map((column, index) =>
        InboxKanbanAPI.updateColumn(effectiveInboxId.value, column.id, {
          position: index,
          name: column.name,
          mapped_status: column.mapped_status,
          color: column.color,
          active: column.active,
        })
      )
    );
  } catch {
    useAlert('Could not reorder columns');
    await fetchBoard();
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => inboxes.value,
  value => {
    if (!selectedInboxId.value && value?.length) {
      selectedInboxId.value = value[0].id;
      fetchBoard();
    }
  },
  { immediate: true }
);

watch(
  () => props.inboxId,
  value => {
    if (value) {
      selectedInboxId.value = value;
      fetchBoard();
    }
  }
);

onMounted(fetchBoard);

defineExpose({ fetchBoard });
</script>

<template>
  <section class="flex flex-col h-[calc(100%-56px)] w-full overflow-hidden">
    <div class="px-3 py-2 border-b border-n-weak flex gap-2 items-center">
      <select
        v-model="selectedInboxId"
        class="rounded px-2 py-1 border border-n-weak bg-n-surface-2 text-sm"
        @change="fetchBoard"
      >
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>

      <div v-if="isAdmin" class="flex gap-2 flex-1">
        <input
          v-model="newColumnName"
          type="text"
          class="flex-1 rounded px-2 py-1 border border-n-weak bg-n-surface-2"
          :placeholder="$t('INBOX.KANBAN.ADD_COLUMN_PLACEHOLDER')"
        />
        <NextButton
          sm
          :is-loading="isSaving"
          :label="$t('INBOX.KANBAN.ADD_COLUMN')"
          @click="addColumn"
        />
      </div>
    </div>

    <div v-if="isLoading" class="p-4 text-sm text-n-slate-11">
      {{ $t('INBOX.KANBAN.LOADING') }}
    </div>

    <draggable
      v-else
      v-model="columns"
      item-key="id"
      class="flex h-full w-full gap-3 p-3 overflow-x-auto"
      handle=".kanban-column-handle"
      :disabled="!isAdmin"
      @end="onColumnsReordered"
    >
      <template #item="{ element: column }">
        <article class="w-80 min-w-80 bg-n-surface-2 rounded-lg border border-n-weak flex flex-col h-full">
          <header class="p-2 border-b border-n-weak space-y-2">
            <div class="flex items-center gap-2">
              <span class="kanban-column-handle i-lucide-grip text-n-slate-10" />
              <input
                v-if="isAdmin"
                v-model="column.name"
                type="text"
                class="flex-1 rounded px-2 py-1 border border-n-weak bg-n-surface-1"
                @blur="updateColumn(column)"
              />
              <h3 v-else class="text-sm font-medium text-n-slate-12">
                {{ column.name }}
              </h3>
              <button
                v-if="isAdmin"
                class="text-n-ruby-9"
                @click="deleteColumn(column)"
              >
                <span class="i-lucide-trash size-4" />
              </button>
            </div>
            <select
              v-if="isAdmin"
              v-model="column.mapped_status"
              class="w-full rounded px-2 py-1 border border-n-weak bg-n-surface-1 text-sm"
              @change="updateColumn(column)"
            >
              <option v-for="status in statusOptions" :key="status" :value="status">
                {{ status }}
              </option>
            </select>
          </header>

          <draggable
            :list="column.conversations"
            item-key="id"
            class="flex-1 p-2 space-y-2 overflow-y-auto"
            group="kanban-conversations"
            @add="onCardAdded($event, column)"
          >
            <template #item="{ element: conversation }">
              <button
                class="w-full text-left p-2 rounded border border-n-weak bg-n-surface-1 hover:bg-n-alpha-1"
                :class="{
                  'ring-1 ring-n-brand': activeConversationId === conversation.id,
                }"
                @click="openConversation(conversation)"
              >
                <p class="text-sm font-medium text-n-slate-12 truncate">
                  #{{ conversation.id }} - {{ conversation.contact?.name || $t('INBOX.KANBAN.UNKNOWN_CONTACT') }}
                </p>
                <p class="text-xs text-n-slate-11 mt-1 line-clamp-2">
                  {{ conversation.last_message || $t('INBOX.KANBAN.NO_MESSAGES') }}
                </p>
                <p class="text-xs text-n-slate-10 mt-1">
                  {{ $t('INBOX.KANBAN.UNREAD') }}: {{ conversation.unread_count }}
                </p>
              </button>
            </template>
          </draggable>
        </article>
      </template>
    </draggable>
  </section>
</template>
